import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { Button } from 'src/shared/components/ui/button';
import Alert, { type AlertSeverity } from 'src/shared/components/ui/alert';
import { useCustomersQuery } from 'src/app/customers/state/api/customersGraphSlice';
import StatsPanel from 'src/app/admin/components/stats-panel/StatsPanel';
import type { EnsureOrganisationOutcome } from 'src/shared/state/api-slices/organisationEngineSlice';
import { useListAllOrganisationsQuery } from './state/api/estateManagementEngineSlice';
import { findEstateDiscrepancies } from './findEstateDiscrepancies';
import { useBulkOnboarding } from './useBulkOnboarding';
import BulkOnboardButton from './components/BulkOnboardButton';
import BulkOnboardSheet from './components/BulkOnboardSheet';
import DiscrepancyTable from './components/DiscrepancyTable';
import type { OnboardResult } from './components/OnboardButton';
import SyncHealth from './components/SyncHealth';

type Notice = { severity: AlertSeverity; message: string };

const onboardNotice = (customerId: string, outcome: EnsureOrganisationOutcome): Notice => {
    if (outcome === 'created') {
        return { severity: 'success', message: `${customerId} is onboarded into Sunrise.` };
    } else if (outcome === 'active') {
        return { severity: 'success', message: `${customerId} was already in Sunrise.` };
    } else {
        return { severity: 'warning', message: `${customerId} is archived in Sunrise. It is listed in Out of sync.` };
    }
};

const EstateManagement = () => {
    const customersQuery = useCustomersQuery({});
    const organisationsQuery = useListAllOrganisationsQuery();
    const [notice, setNotice] = useState<Notice | null>(null);
    const bulk = useBulkOnboarding(() => organisationsQuery.refetch());
    const [isBulkOpen, setIsBulkOpen] = useState(false);

    const closeBulk = () => {
        setIsBulkOpen(false);
        bulk.clear();
    };

    const handleOnboarded = (customerId: string, result: OnboardResult) => {
        if (result === 'failed') {
            setNotice({ severity: 'error', message: `${customerId} could not be onboarded. Try again.` });
        } else {
            setNotice(onboardNotice(customerId, result));
            organisationsQuery.refetch();
        }
    };

    const mainContent = (() => {
        if (customersQuery.isLoading || organisationsQuery.isLoading) {
            return (
                <div className='flex justify-center py-24'>
                    <Loader />
                </div>
            );
        }

        if (customersQuery.error || organisationsQuery.error) {
            return (
                <div className='flex flex-col items-center justify-center py-24 gap-4 text-center' role='alert'>
                    <div className='flex items-center justify-center w-16 h-16 rounded-full bg-red-0'>
                        <AlertCircle className='w-8 h-8 text-red-100' />
                    </div>
                    <div className='flex flex-col items-center gap-1 max-w-sm'>
                        <h3 className='text-lg font-semibold text-primary'>Unable to compare organisations</h3>
                        <p className='text-sm text-grey-70'>The platform or the engine did not return its organisations. Try again.</p>
                    </div>
                    <Button
                        variant='outline'
                        onClick={() => {
                            customersQuery.refetch();
                            organisationsQuery.refetch();
                        }}
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        const customers = customersQuery.data?.customers ?? [];
        const organisations = organisationsQuery.data ?? [];
        const discrepancies = findEstateDiscrepancies(customers, organisations);
        const activeOrganisations = organisations.filter(organisation => organisation.deleted_at === null).length;
        const archivedOrganisations = organisations.length - activeOrganisations;
        const discrepancyCount =
            discrepancies.missingInEngine.length +
            discrepancies.archivedInEngine.length +
            discrepancies.engineOnly.length +
            discrepancies.nameDiffers.length;

        return (
            <div className='flex flex-col gap-8 pb-6'>
                <StatsPanel
                    title='What the estate holds'
                    isLoading={false}
                    figures={[
                        { label: 'Customers in Sunset', value: customers.length },
                        {
                            label: 'Organisations in Sunrise',
                            value: activeOrganisations,
                            note: archivedOrganisations > 0 && (
                                <span className='text-sm text-grey-70'>+ {archivedOrganisations} archived</span>
                            ),
                        },
                        { label: 'Discrepancies', value: discrepancyCount, note: <SyncHealth discrepancies={discrepancyCount} /> },
                    ]}
                />
                <BulkOnboardSheet
                    open={isBulkOpen}
                    count={discrepancies.missingInEngine.length}
                    run={bulk.run}
                    onConfirm={() => bulk.start(discrepancies.missingInEngine)}
                    onStop={bulk.stop}
                    onClose={closeBulk}
                />
                {notice && <Alert severity={notice.severity}>{notice.message}</Alert>}
                <DiscrepancyTable
                    discrepancies={discrepancies}
                    onOnboarded={handleOnboarded}
                    sunsetOnlyActions={
                        discrepancies.missingInEngine.length > 0 && (
                            <BulkOnboardButton count={discrepancies.missingInEngine.length} onClick={() => setIsBulkOpen(true)} />
                        )
                    }
                />
            </div>
        );
    })();

    return (
        <StandardAppLayout
            title='Estate Management'
            subTitle='Sunset is the legacy platform. Sunrise is the engine.'
            stickyHeader={false}
            mainContent={mainContent}
        />
    );
};

export default EstateManagement;
